import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";

import useDeflowSessionStore from "@/stores/useDeflowSessionStore";
import useFigmaSessionStore from "@/stores/useFigmaSessionStore";
import { cellConfig } from "@/lib/cell";
import {
  APIAccount,
  APIElement,
  APIFigmaAuth,
  APIPage,
  APISite,
  ApexDnsGuidance,
  CustomHostDomainStatus,
  DnsInstruction,
  Element,
  Site,
  SiteOrigin,
  SiteOriginConfig,
} from "deflow-models";

export type FetchError = {
  message: string;
  error: string;
  statusCode: number;
};

// Resolves the API base URL from the active cell. Called at request time
// (not module load) so users who switch cells mid-session reach the right
// backend on subsequent requests.
const apiBase = () => cellConfig().apiUrl;

const parseResponseJson = async (res: Response) => {
  const text = await res.text();
  if (!text) {
    return undefined;
  }
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
};

const get = async ({
  url,
  token,
  cache,
}: {
  url: string;
  token: string;
  cache?: RequestCache;
}) => {
  const response = await fetch(apiBase() + url, {
    headers: {
      Authorization: "Bearer " + token,
    },
    cache: cache ? cache : undefined,
  });

  return await parseResponseJson(response);
};

async function putOrThrow<TData, TError = FetchError>(args: {
  url: string;
  token: string;
  body?: Record<string, any>;
}) {
  const [data, error] = await put<TData, TError>(args);
  if (error) {
    throw error;
  }
  return data;
}

const put = <TData = any, TError = any>({
  url,
  token,
  body,
}: {
  url: string;
  token: string;
  body?: Record<string, any>;
}): Promise<[TData, TError]> =>
  fetch(apiBase() + url, {
    method: "PUT",
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  }).then(async (res) => {
    const json = await parseResponseJson(res);
    if (res.ok) {
      return [json as TData, undefined as unknown as TError];
    }
    const error =
      json ??
      ({
        message: res.statusText || "Request failed",
        error: "true",
        statusCode: res.status,
      } as TError);
    return [undefined as unknown as TData, error];
  });

const post = <TData = any, TError = any>({
  url,
  token,
  body,
  headers,
}: {
  url: string;
  token: string;
  body?: Record<string, any>;
  headers?: Record<string, any>;
}): Promise<[TData, TError]> =>
  fetch(apiBase() + url, {
    method: "POST",
    headers: {
      Authorization: "Bearer " + token,
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...(headers ? headers : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  }).then(async (res) => {
    const json = await parseResponseJson(res);
    if (res.ok) {
      return [json as TData, undefined as unknown as TError];
    }
    const error =
      json ??
      ({
        message: res.statusText || "Request failed",
        error: "true",
        statusCode: res.status,
      } as TError);
    return [undefined as unknown as TData, error];
  });

const del = ({ url, token }: { url: string; token: string }) =>
  fetch(apiBase() + url, {
    method: "DELETE",
    headers: {
      Authorization: "Bearer " + token,
    },
  });

// Stable, typed cache keys. Tokens are intentionally excluded — they
// rotate on refresh but the user is still the same. Sign-out clears the
// entire cache via queryClient.clear() in routes/_authenticated/sign-out.tsx.
export const queryKeys = {
  account: () => ["account"] as const,
  figmaAuth: () => ["account", "figma-auth"] as const,
  sites: () => ["sites"] as const,
  site: (siteId: string) => ["sites", siteId] as const,
  pages: (siteId: string) => ["sites", siteId, "pages"] as const,
  elements: (siteId: string, pageId: string) =>
    ["sites", siteId, "pages", pageId, "elements"] as const,
};

// Adapter that maps useQuery's result to the SWR-shaped names that the
// rest of the codebase already destructures (`data`, `error`, `isLoading`,
// `mutate`). `mutate()` invalidates this query's key, mirroring SWR's
// imperative refetch behavior.
function useAdaptedQuery<TData, TError = unknown>(
  options: UseQueryOptions<TData, TError, TData, QueryKey> & {
    queryKey: QueryKey;
  },
) {
  const queryClient = useQueryClient();
  const result = useQuery<TData, TError, TData, QueryKey>(options);
  return {
    ...result,
    mutate: () =>
      queryClient.invalidateQueries({ queryKey: options.queryKey }),
  };
}

// Adapter that exposes useMutation under SWR's `trigger` / `isMutating`
// names. The `mutationFn` receives a single argument that callers pass to
// `trigger(arg)`, matching `useSWRMutation`'s contract.
function useAdaptedMutation<TData, TVariables, TError = unknown>(
  options: UseMutationOptions<TData, TError, TVariables>,
) {
  const result = useMutation<TData, TError, TVariables>(options);
  return {
    ...result,
    trigger: result.mutateAsync,
    isMutating: result.isPending,
  };
}

export const useAccount = () => {
  const { tokens } = useDeflowSessionStore();
  return useAdaptedQuery<APIAccount>({
    queryKey: queryKeys.account(),
    queryFn: () => get({ url: "/v1/account", token: tokens!.accessToken }),
    enabled: !!tokens?.accessToken,
  });
};

export const useSites = () => {
  const { tokens } = useDeflowSessionStore();
  return useAdaptedQuery<APISite[]>({
    queryKey: queryKeys.sites(),
    queryFn: () => get({ url: "/v1/sites", token: tokens!.accessToken }),
    enabled: !!tokens?.accessToken,
  });
};

export const useCreateSite = () => {
  const { tokens } = useDeflowSessionStore();
  const queryClient = useQueryClient();
  return useAdaptedMutation<
    [APISite, FetchError],
    { siteName: string; origin?: SiteOrigin; originConfig?: SiteOriginConfig }
  >({
    mutationFn: (body) =>
      post({
        url: "/v1/sites",
        token: tokens!.accessToken,
        body,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sites() });
    },
  });
};

export const useUpdateSite = (siteId?: string) => {
  const { tokens } = useDeflowSessionStore();
  const queryClient = useQueryClient();
  return useAdaptedMutation<
    APISite,
    Partial<
      Pick<
        Site,
        | "siteName"
        | "siteHost"
        | "customSiteHost"
        | "customHostEnabled"
        | "redirectApexToWww"
        | "cspConfig"
      > & {
        figmaFileKey?: string;
        originConfig?: SiteOriginConfig;
        origin?: SiteOrigin;
      }
    >,
    FetchError & { retryAfterSeconds?: number }
  >({
    mutationFn: (body) =>
      putOrThrow<APISite, FetchError & { message?: string; retryAfterSeconds?: number }>({
        url: `/v1/sites/${siteId}`,
        token: tokens!.accessToken,
        body,
      }),
    onSuccess: () => {
      if (siteId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.site(siteId) });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.sites() });
    },
  });
};

export type SiteWithCustomHostDetails = APISite & {
  dnsInstructions?: DnsInstruction[];
  apexDnsGuidance?: ApexDnsGuidance;
  customHostDomains?: CustomHostDomainStatus[];
};

export const useSite = (siteId?: string) => {
  const { tokens } = useDeflowSessionStore();
  return useAdaptedQuery<SiteWithCustomHostDetails>({
    queryKey: siteId ? queryKeys.site(siteId) : ["sites", "__none__"],
    queryFn: () =>
      get({ url: `/v1/sites/${siteId}`, token: tokens!.accessToken }),
    enabled: !!tokens?.accessToken && !!siteId,
  });
};

export type RefreshCustomHostResponse = {
  site: SiteWithCustomHostDetails;
};

export const useRefreshSiteCustomHost = (siteId?: string) => {
  const { tokens } = useDeflowSessionStore();
  const queryClient = useQueryClient();
  return useAdaptedMutation<
    [RefreshCustomHostResponse, FetchError],
    void
  >({
    mutationFn: () =>
      post({
        url: `/v1/sites/${siteId}/custom-host/refresh`,
        token: tokens!.accessToken,
      }),
    onSuccess: ([data]) => {
      if (siteId && data?.site) {
        queryClient.setQueryData<SiteWithCustomHostDetails>(
          queryKeys.site(siteId),
          (current) => (current ? { ...current, ...data.site } : data.site)
        );
      }
    },
  });
};

export const usePublishSite = (siteId?: string) => {
  const { tokens } = useDeflowSessionStore();
  const { tokens: figmaTokens } = useFigmaSessionStore();
  const queryClient = useQueryClient();
  return useAdaptedMutation<
    [APISite, FetchError],
    | { uploadVersion: number }
    | void
  >({
    mutationFn: (vars) => {
      const isUploadFinalize =
        typeof vars === "object" &&
        vars !== null &&
        typeof (vars as { uploadVersion?: unknown }).uploadVersion ===
          "number";

      return post({
        url: `/v1/sites/${siteId}/publish`,
        token: tokens!.accessToken,
        body:
          isUploadFinalize
            ? { uploadVersion: (vars as { uploadVersion: number }).uploadVersion }
            : undefined,
        headers: isUploadFinalize
          ? {}
          : { "x-figma-access-token": figmaTokens!.accessToken },
      });
    },
    onSuccess: () => {
      if (siteId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.site(siteId) });
      }
    },
  });
};

export const useStartSiteUpload = (siteId?: string) => {
  const { tokens } = useDeflowSessionStore();
  const queryClient = useQueryClient();
  return useAdaptedMutation<
    [
      {
        uploadVersion: number;
        uploads: Array<{
          path: string;
          uploadUrl: string;
          contentType: string;
        }>;
      },
      FetchError
    ],
    { uploads: Array<{ path: string; contentType?: string }> }
  >({
    mutationFn: (body) =>
      post({
        url: `/v1/sites/${siteId}/uploads`,
        token: tokens!.accessToken,
        body,
      }),
    onSuccess: () => {
      if (siteId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.site(siteId) });
      }
    },
  });
};

export const usePages = (siteId?: string) => {
  const { tokens } = useDeflowSessionStore();
  return useAdaptedQuery<APIPage[]>({
    queryKey: siteId ? queryKeys.pages(siteId) : ["sites", "__none__", "pages"],
    queryFn: () =>
      get({ url: `/v1/sites/${siteId}/pages`, token: tokens!.accessToken }),
    enabled: !!tokens?.accessToken && !!siteId,
  });
};

export const useCreatePage = (siteId?: string) => {
  const { tokens } = useDeflowSessionStore();
  const queryClient = useQueryClient();
  return useAdaptedMutation<[APIPage, FetchError], Partial<APIPage>>({
    mutationFn: (body) =>
      post({
        url: `/v1/sites/${siteId}/pages`,
        token: tokens!.accessToken,
        body,
      }),
    onSuccess: () => {
      if (siteId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.pages(siteId) });
      }
    },
  });
};

export const useUpdatePage = (siteId?: string, pageId?: string) => {
  const { tokens } = useDeflowSessionStore();
  const queryClient = useQueryClient();
  return useAdaptedMutation<[APIPage, FetchError], Partial<APIPage>>({
    mutationFn: (body) =>
      putOrThrow({
        url: `/v1/sites/${siteId}/pages/${pageId}`,
        token: tokens!.accessToken,
        body,
      }).then((page) => [page, undefined as unknown as FetchError] as [APIPage, FetchError]),
    onSuccess: () => {
      if (siteId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.pages(siteId) });
      }
    },
  });
};

export const useDeletePage = (siteId?: string, pageId?: string) => {
  const { tokens } = useDeflowSessionStore();
  const queryClient = useQueryClient();
  return useAdaptedMutation<Response, void>({
    mutationFn: () =>
      del({
        url: `/v1/sites/${siteId}/pages/${pageId}`,
        token: tokens!.accessToken,
      }),
    onSuccess: () => {
      if (siteId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.pages(siteId) });
      }
    },
  });
};

export const useElements = (siteId?: string, pageId?: string) => {
  const { tokens } = useDeflowSessionStore();
  return useAdaptedQuery<APIElement[]>({
    queryKey:
      siteId && pageId
        ? queryKeys.elements(siteId, pageId)
        : ["sites", "__none__", "pages", "__none__", "elements"],
    queryFn: () =>
      get({
        url: `/v1/sites/${siteId}/pages/${pageId}/elements`,
        token: tokens!.accessToken,
      }),
    enabled: !!tokens?.accessToken && !!siteId && !!pageId,
  });
};

export const useCreateElement = (siteId?: string, pageId?: string) => {
  const { tokens } = useDeflowSessionStore();
  const queryClient = useQueryClient();
  return useAdaptedMutation<[APIElement, FetchError], Partial<Element>>({
    mutationFn: (body) =>
      post({
        url: `/v1/sites/${siteId}/pages/${pageId}/elements`,
        token: tokens!.accessToken,
        body,
      }),
    onSuccess: () => {
      if (siteId && pageId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.elements(siteId, pageId),
        });
      }
    },
  });
};

export const useUpdateElement = (
  siteId?: string,
  pageId?: string,
  elementId?: string,
) => {
  const { tokens } = useDeflowSessionStore();
  const queryClient = useQueryClient();
  return useAdaptedMutation<[APIElement, FetchError], Partial<Element>>({
    mutationFn: (body) =>
      putOrThrow({
        url: `/v1/sites/${siteId}/pages/${pageId}/elements/${elementId}`,
        token: tokens!.accessToken,
        body,
      }).then(
        (element) =>
          [element, undefined as unknown as FetchError] as [APIElement, FetchError],
      ),
    onSuccess: () => {
      if (siteId && pageId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.elements(siteId, pageId),
        });
      }
    },
  });
};

export const useFigmaAuth = () => {
  const { tokens } = useDeflowSessionStore();
  return useAdaptedQuery<APIFigmaAuth>({
    queryKey: queryKeys.figmaAuth(),
    queryFn: () =>
      get({ url: "/v1/account/figma-auth", token: tokens!.accessToken }),
    enabled: !!tokens?.accessToken,
  });
};

export const useUpdateAccount = () => {
  const { tokens } = useDeflowSessionStore();
  const queryClient = useQueryClient();
  return useAdaptedMutation<
    APIAccount,
    { firstName: string; lastName: string },
    FetchError
  >({
    mutationFn: (body) =>
      putOrThrow({ url: "/v1/account", token: tokens!.accessToken, body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.account() });
    },
  });
};

export const usePostOAuth2CallbackParams = (token?: string) => {
  return useAdaptedMutation<
    [{ success: boolean }, FetchError],
    { code: string; state: string }
  >({
    mutationFn: (body) =>
      post({ url: "/v1/oauth2/authorize", token: token!, body }),
  });
};

export const Deflow = {
  get,
};
